import * as THREE from 'three';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

/**
 * Full-screen pass: chromatic separation + radial smear + vignette + cop-shot red lift.
 * Driven by `CameraDirectorOutput` each frame for a cinematic, high-end chase look.
 */
export function createDirectorPostShaderPass(): ShaderPass {
  const shader = {
    name: 'DirectorPostShader',
    uniforms: {
      tDiffuse: { value: null as THREE.Texture | null },
      chromaticAmt: { value: 0 },
      radialBlurAmt: { value: 0 },
      vignetteAmt: { value: 0 },
      redTintAmt: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D tDiffuse;
      uniform float chromaticAmt;
      uniform float radialBlurAmt;
      uniform float vignetteAmt;
      uniform float redTintAmt;
      varying vec2 vUv;

      void main() {
        vec2 uv = vUv;
        vec2 c = vec2(0.5);
        vec2 d = uv - c;
        float dist = length(d);

        vec4 blurred = vec4(0.0);
        float wsum = 0.0;
        float blur = radialBlurAmt * 0.052;
        for (int i = 0; i < 7; i++) {
          float fi = float(i);
          float t = fi / 6.0;
          float w = 1.0 - t * 0.55;
          vec2 suv = uv - d * blur * t;
          suv = clamp(suv, 0.001, 0.999);
          blurred += texture2D(tDiffuse, suv) * w;
          wsum += w;
        }
        blurred /= wsum;

        float ca = chromaticAmt * 0.016;
        vec2 off = vec2(ca, ca * 0.58);
        vec2 uR = clamp(uv + off, 0.001, 0.999);
        vec2 uG = clamp(uv, 0.001, 0.999);
        vec2 uB = clamp(uv - off, 0.001, 0.999);
        float r = texture2D(tDiffuse, uR).r;
        float g = texture2D(tDiffuse, uG).g;
        float b = texture2D(tDiffuse, uB).b;
        vec3 rgb = vec3(r, g, b);
        rgb = mix(rgb, blurred.rgb, clamp(radialBlurAmt * 0.62, 0.0, 0.92));

        float vig = 1.0 - vignetteAmt * smoothstep(0.12, 0.95, dist * 1.22);
        rgb *= vig;
        rgb.r += redTintAmt * (1.0 - vig);

        gl_FragColor = vec4(rgb, 1.0);
      }
    `,
  };

  return new ShaderPass(shader);
}
